import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { humanSize, timeAgo } from "@/lib/format";
import { queryKeys } from "@/lib/queryKeys";
import { AttachmentUpload } from "./AttachmentUpload";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { Icon } from "./Icon";
import { toast } from "./Toast";
import type { AttachmentRow } from "@shared/types";

/**
 * Raw redirect endpoint: 302s to a presigned R2 URL for this attachment.
 * Using it as the `<a href>` lets the browser handle the click natively,
 * so popup-blockers don't kill the new tab the way they did with the old
 * `await getAttachment(id); window.open(r.url)` flow.
 */
function rawUrl(id: string): string {
  return `/api/attachments/${id}/raw`;
}

/**
 * AttachmentList — designs/TaskDetail.html `.blk` + `.attach` row.
 *
 * Renders one `.attach` row per attachment with mime-coloured icon,
 * filename, uploader meta, and human-readable size. Clicking a row
 * opens the file in a new tab via the presigned `getAttachment` URL.
 */

/** Mime-type → icon-bubble colour. Matches the design's red/blue swatches. */
function iconStyle(mime: string): { background: string; color: string } {
  if (mime.startsWith("image/"))
    return { background: "#eaf6ee", color: "#1f8a5b" };
  if (mime.includes("pdf")) return { background: "#fdecec", color: "#dc3b32" };
  if (mime.includes("word") || mime.includes("document"))
    return { background: "#eaf1fb", color: "#3b82d4" };
  return { background: "var(--st-todo-bg)", color: "var(--st-todo-ink)" };
}

export function AttachmentList({
  taskId,
  projectId,
  ownerId,
}: {
  taskId: string;
  /** Enables the full invalidateForTask sweep (attachment counts on cards). */
  projectId?: string;
  /** Project owner id — owners may delete any member's attachment (T43). */
  ownerId?: string | null;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.attachmentsForTask(taskId),
    queryFn: () => api.listAttachments(taskId),
  });
  const me = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const myId = me.data?.user?.id;
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => api.deleteAttachment(id),
    onSuccess: () => {
      setConfirmId(null);
      qc.invalidateQueries({ queryKey: queryKeys.attachmentsForTask(taskId) });
      qc.invalidateQueries({ queryKey: queryKeys.files() });
      // Attachment counts ride on the task list rows — full sweep per the
      // cache-invalidation discipline in ARCHITECTURE.md.
      invalidateForTask(qc, { projectId, taskId });
    },
    onError: () => {
      setConfirmId(null);
      toast.error("Couldn't delete the file. Try again.");
    },
  });

  const attachments: AttachmentRow[] = data?.attachments ?? [];

  return (
    <section className="blk" aria-label="Attachments">
      <h3>
        Attachments
        <span className="cnt">{attachments.length}</span>
      </h3>
      {isLoading && <p className="t-sm t-muted">Loading attachments…</p>}
      {isError && <p className="t-sm t-muted">Couldn't load attachments.</p>}
      {!isLoading && !isError && attachments.length === 0 && (
        <p className="t-sm t-muted">No attachments yet.</p>
      )}
      {attachments.map((a) => {
        const swatch = iconStyle(a.mime_type);
        const canDelete =
          myId != null && (myId === a.uploaded_by || myId === ownerId);
        return (
          <div key={a.id} className="attach">
            <a
              className="a-link"
              href={rawUrl(a.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="a-ic" style={swatch}>
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path
                    d="M5 3.5h6l4 4v9a1 1 0 01-1 1H5a1 1 0 01-1-1v-12a1 1 0 011-1z"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11 3.5V7.5h4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <div className="a-nm">{a.filename}</div>
                <div className="a-meta">
                  Added by {a.uploaded_by} · {timeAgo(a.uploaded_at)}
                </div>
              </div>
            </a>
            <span className="a-size">{humanSize(a.size_bytes)}</span>
            {canDelete &&
              (confirmId === a.id ? (
                <span className="a-del-confirm">
                  <button
                    type="button"
                    className="a-del-yes"
                    disabled={del.isPending}
                    onClick={() => del.mutate(a.id)}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="a-del-no"
                    onClick={() => setConfirmId(null)}
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="a-del"
                  aria-label={`Delete ${a.filename}`}
                  title="Delete file"
                  onClick={() => setConfirmId(a.id)}
                >
                  <Icon name="close" size={12} />
                </button>
              ))}
          </div>
        );
      })}
      <div style={{ marginTop: 8 }}>
        <AttachmentUpload taskId={taskId} />
      </div>
    </section>
  );
}
